<template>
  <div
    class="
      col-span-6
      sm:col-span-3
      xl:col-span-2
      flex flex-col
      justify-end
      items-center
    "
    v-if="loading"
  >
    <LoadingIcon icon="three-dots" class="w-8 h-8" />
  </div>
  <div v-if="!loading">
    <div class="intro-y flex flex-col sm:flex-row items-center mt-8">
      <UsersIcon />
      <h2 class="text-lg font-medium mr-auto">
        {{ $t('employee.plan') }} - {{ employee.name }} {{ employee.lastname }}
      </h2>
      <div class="w-full sm:w-auto flex mt-4 sm:mt-0">
        <router-link
          :to="{ name: 'side-menu-employees' }"
          tag="a"
          class="btn shadow-md flex items-center"
        >
          <ArrowLeftCircleIcon class="w-4 h-4 mr-2" /> {{ $t('common.back') }}
        </router-link>
      </div>
    </div>
    <div class="grid grid-cols-12 gap-5 mt-5">
      <!-- BEGIN: Calendar Side Menu -->
      <div class="col-span-12 xl:col-span-4 2xl:col-span-3">
        <div class="box p-5 intro-y">
          <form class="validate-form" @submit.prevent="handleSave">
            <div class="intro-y box p-5">
              <div class="mt-3">
                <label
                  for="timeFrom"
                  class="form-label w-full flex flex-col sm:flex-row"
                >
                  {{ $t('form.field.timeFrom') }}
                  <span class="sm:ml-auto mt-1 sm:mt-0 text-xs text-gray-600">
                    {{ $t('form.required') }}
                  </span>
                </label>
                <input
                  id="timeFrom"
                  v-model.trim="validate.timeFrom.$model"
                  type="time"
                  name="timeFrom"
                  class="
                    intro-y
                    form-control
                    py-3
                    px-4
                    box
                    pr-10
                    placeholder-theme-13
                  "
                  :class="{
                    'border-theme-6': validate.timeFrom.$error
                  }"
                  placeholder="08:00"
                />
                <template v-if="validate.timeFrom.$error">
                  <div
                    v-for="(error, index) in validate.timeFrom.$errors"
                    :key="index"
                    class="text-theme-6 mt-2"
                  >
                    {{ $t(error.$message) }}
                  </div>
                </template>
              </div>
              <div class="mt-3">
                <label
                  for="timeTo"
                  class="form-label w-full flex flex-col sm:flex-row"
                >
                  {{ $t('form.field.timeTo') }}
                  <span class="sm:ml-auto mt-1 sm:mt-0 text-xs text-gray-600">
                    {{ $t('form.required') }}
                  </span>
                </label>
                <input
                  id="timeTo"
                  v-model.trim="validate.timeTo.$model"
                  type="time"
                  name="timeTo"
                  class="
                    intro-y
                    form-control
                    py-3
                    px-4
                    box
                    pr-10
                    placeholder-theme-13
                  "
                  :class="{ 'border-theme-6': validate.timeTo.$error }"
                  placeholder="08:00"
                />
                <template v-if="validate.timeTo.$error">
                  <div
                    v-for="(error, index) in validate.timeTo.$errors"
                    :key="index"
                    class="text-theme-6 mt-2"
                  >
                    {{ $t(error.$message) }}
                  </div>
                </template>
              </div>
              <div class="mt-3">
                <BranchSelect v-model:selectedBranch="selectedBranch" />
              </div>
              <div class="mt-3">
                <label for="rangeView" class="form-check-label ml-0 mb-2">
                  {{ $t('common.range') }}
                </label>
                <input
                  id="rangeView"
                  v-model="rangeView"
                  class="form-check-switch"
                  type="checkbox"
                />
              </div>
              <div class="mt-3" v-if="!rangeView">
                <label
                  for="date"
                  class="form-label w-full flex flex-col sm:flex-row"
                >
                  {{ $t('common.date') }}
                  <span class="sm:ml-auto mt-1 sm:mt-0 text-xs text-gray-600">
                    {{ $t('form.required') }}
                  </span>
                </label>
                <div class="relative w-56 mx-auto">
                  <div
                    class="absolute rounded-l w-10 h-full flex items-center justify-center bg-gray-100 border text-gray-600 dark:bg-dark-1 dark:border-dark-4"
                  >
                    <CalendarIcon class="w-4 h-4" />
                  </div>
                  <Litepicker
                    v-model="date"
                    :options="{
                      autoApply: false,
                      singleMode: true,
                      numberOfColumns: 2,
                      numberOfMonths: 2,
                      minDate: dateConstraint,
                      dropdowns: {
                        minYear: 2023,
                        maxYear: null,
                        months: true,
                        years: true
                      },
                      lang: 'sk-SK'
                    }"
                    class="form-control pl-12 w-56 block mx-auto"
                  />
                </div>
              </div>
              <div class="mt-3" v-if="rangeView">
                <DaySelector v-model="selectedDays" />
              </div>
              <div class="mt-3" v-if="rangeView">
                <div class="relative w-56 mx-auto">
                  <div
                    class="absolute rounded-l w-10 h-full flex items-center justify-center bg-gray-100 border text-gray-600 dark:bg-dark-1 dark:border-dark-4"
                  >
                    <CalendarIcon class="w-4 h-4" />
                  </div>
                  <Litepicker
                    v-model="daterange"
                    :options="{
                      autoApply: false,
                      singleMode: false,
                      numberOfColumns: 2,
                      numberOfMonths: 2,
                      minDate: dateConstraint,
                      dropdowns: {
                        minYear: 2023,
                        maxYear: null,
                        months: true,
                        years: true
                      },
                      lang: 'sk-SK'
                    }"
                    class="form-control pl-12 w-56 block mx-auto"
                  />
                </div>
              </div>
              <div class="mt-3">
                <button
                  type="submit"
                  class="btn btn-primary shadow-md flex items-center"
                  v-if="!saving"
                >
                  <SaveIcon class="w-4 h-4 mr-2" /> {{ $t('common.save') }}
                </button>
                <div
                  class="
                    col-span-6
                    sm:col-span-3
                    xl:col-span-2
                    flex flex-col
                    justify-end
                    items-center
                  "
                  v-if="saving"
                >
                  <LoadingIcon icon="three-dots" class="w-8 h-8" />
                </div>
              </div>
              <div class="mt-3">
                <button
                  type="button"
                  class="btn btn-warning shadow-md flex items-center"
                  @click="showDeleteAccessModal()"
                  v-if="!saving"
                >
                  <DeleteIcon class="w-4 h-4 mr-2" /> {{ $t('common.remove') }}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
      <!-- END: Calendar Side Menu -->
      <!-- BEGIN: Calendar Content -->
      <div class="col-span-12 xl:col-span-8 2xl:col-span-9">
        <div class="box p-5">
          <PlanCalendar
            ref="planCalendar"
            :employeeUUID="this.$route.params.uuid"
          />
        </div>
      </div>
      <!-- END: Calendar Content -->
    </div>
  </div>
  <!-- BEGIN: Success Notification Content -->
  <div id="success-notification-content" class="toastify-content hidden flex">
    <CheckCircleIcon class="text-theme-9" />
    <div class="ml-4 mr-4">
      <div class="font-medium">{{ $t('employee.planCreated') }}!</div>
      <!-- <div class="text-gray-600 mt-1">
        Please check your e-mail for further info!
      </div> -->
    </div>
  </div>
  <!-- END: Success Notification Content -->
  <!-- BEGIN: Failed Notification Content -->
  <div id="failed-notification-content" class="toastify-content hidden flex">
    <XCircleIcon class="text-theme-6" />
    <div class="ml-4 mr-4">
      <div class="font-medium">{{ $t('employee.planNotCreated') }}!</div>
      <div class="text-gray-600 mt-1">{{ apiError }}</div>
    </div>
  </div>
  <!-- END: Failed Notification Content -->
  <!-- BEGIN: Success Notification Content -->
  <div id="success-delete-range-notification-content" class="toastify-content hidden flex">
    <CheckCircleIcon class="text-theme-9" />
    <div class="ml-4 mr-4">
      <div class="font-medium">{{ $t('employee.planDeleted') }}!</div>
      <!-- <div class="text-gray-600 mt-1">
        Please check your e-mail for further info!
      </div> -->
    </div>
  </div>
  <!-- END: Success Notification Content -->
  <!-- BEGIN: Failed Notification Content -->
  <div id="failed-delete-range-notification-content" class="toastify-content hidden flex">
    <XCircleIcon class="text-theme-6" />
    <div class="ml-4 mr-4">
      <div class="font-medium">{{ $t('employee.planNotDeleted') }}!</div>
      <div class="text-gray-600 mt-1">{{ apiError }}</div>
    </div>
  </div>
  <!-- END: Failed Notification Content -->
  <!-- BEGIN: Delete Confirm Modal -->
  <div id="delete-range-plan-modal" class="modal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-xl">
      <div class="modal-content">
        <div class="modal-body p-0">
          <div class="p-5 text-left">
            <div class="text-3xl mt-5">
              {{ $t('employee.deletePlanRangeConfirm') }}
            </div>
            <div class="text-gray-600 mt-2">
              {{ (rangeView ? daterange : date) }}
            </div>
            <div class="text-gray-600 mt-2">
              {{ validate.timeFrom.$model }} - {{ validate.timeTo.$model }}
            </div>
            <div class="text-gray-600 mt-2">
              {{ $t('employee.deletePlanRangeNote') }}
            </div>
            <div
              class="
                col-span-6
                sm:col-span-3
                xl:col-span-2
                flex flex-col
                justify-end
                items-center
              "
              v-if="isRangePlanDeleting"
            >
              <LoadingIcon icon="three-dots" class="w-8 h-8" />
            </div>
          </div>
          <div class="flex mb-3">
            <div class="px-5 pb-8 text-center flex-1">
              <button
                type="button"
                data-dismiss="modal"
                @click="hideDeleteAccessModal()"
                v-if="
                  !this.isRangePlanDeleting
                "
                class="
                btn btn-outline-secondary
                w-40
                dark:border-dark-5 dark:text-gray-300
                mr-1
              "
              >
                {{ $t('common.close') }}
              </button>
            </div>
            <div class="px-5 pb-8 text-center flex-1">
              <button
                type="button"
                class="btn btn-danger w-40"
                v-if="
                  !this.isRangePlanDeleting
                "
                @click="deleteRangePlan()"
              >
                {{ $t('common.cancel') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- END: Delete Confirm Modal -->
</template>

<script>
import { defineComponent, ref, reactive, toRefs, toRaw } from 'vue'
import { required, helpers } from '@vuelidate/validators'
import { useVuelidate } from '@vuelidate/core'
import Toastify from 'toastify-js'
import PlanCalendar from '../../components/employees/PlanCalendar.vue'
import BranchSelect from '@/components/branches/BranchSelect.vue'
import DaySelector from '@/components/employees/DaySelector.vue'
import employeeApi from '@/services/employees/employee.api'

export default defineComponent({
  components: {
    PlanCalendar,
    DaySelector,
    BranchSelect
  },
  methods: {
    showDeleteAccessModal() {
      cash('#delete-range-plan-modal').modal('show')
    },
    hideDeleteAccessModal() {
      cash('#delete-range-plan-modal').modal('hide')
    },
    deleteRangePlan() {
      this.isAccessDeleting = true
      const request = {
        branchUUID: toRaw(this.selectedBranch.selectedBranch) ?? null,
        selectedDays: this.selectedDays ?? null,
        planTypeCode: 'working_plan',
        date: this.rangeView ? null : this.date.replace(/^(\d+)\.(\d+)\.(\d+)$/, '$3-$2-$1'),
        dateFrom: this.rangeView
          ? this.daterange.replace(/^(\d+)\.(\d+)\.(\d+) - \d+\.\d+\.\d+$/, '$3-$2-$1')
          : null,
        dateTo: this.rangeView
          ? this.daterange.replace(/^\d+\.\d+\.\d+ - (\d+)\.(\d+)\.(\d+)$/, '$3-$2-$1')
          : null,
        timeFrom: this.formData.timeFrom,
        timeTo: this.formData.timeTo
      }

      employeeApi.deletePlanRange(this.employee.uuid, request).then(
        response => {
          Toastify({
            node: cash('#success-delete-range-notification-content')
              .clone()
              .removeClass('hidden')[0],
            duration: 5000,
            newWindow: true,
            close: true,
            gravity: 'top',
            position: 'right',
            stopOnFocus: true
          }).showToast()
          this.isAccessDeleting = false
          this.$refs.planCalendar.reloadCalendar()
          this.hideDeleteAccessModal()
        },
        error => {
          console.log(error)
          this.loading = false
          Toastify({
            node: cash('#failed-delete-range-notification-content')
              .clone()
              .removeClass('hidden')[0],
            duration: 10000,
            newWindow: true,
            close: true,
            gravity: 'top',
            position: 'right',
            stopOnFocus: true
          }).showToast()
          this.isAccessDeleting = false
        }
      )
    },
    handleSave(data) {
      this.saving = true
      this.validate.$touch()
      if (this.validate.$invalid) {
        Toastify({
          node: cash('#failed-notification-content')
            .clone()
            .removeClass('hidden')[0],
          duration: 10000,
          newWindow: true,
          close: true,
          gravity: 'top',
          position: 'right',
          stopOnFocus: true
        }).showToast()
        return
      }

      const saveRequest = {
        branchUUID: toRaw(this.selectedBranch.selectedBranch) ?? null,
        selectedDays: this.selectedDays ?? null,
        planTypeCode: 'working_plan',
        date: this.rangeView ? null : this.date.replace(/^(\d+)\.(\d+)\.(\d+)$/, '$3-$2-$1'),
        dateFrom: this.rangeView
          ? this.daterange.replace(/^(\d+)\.(\d+)\.(\d+) - \d+\.\d+\.\d+$/, '$3-$2-$1')
          : null,
        dateTo: this.rangeView
          ? this.daterange.replace(/^\d+\.\d+\.\d+ - (\d+)\.(\d+)\.(\d+)$/, '$3-$2-$1')
          : null,
        timeFrom: data.target.elements.timeFrom.value,
        timeTo: data.target.elements.timeTo.value
      }

      this.apiError = null
      employeeApi
        .createPlan(this.employee.uuid, saveRequest)
        .then(
          response => {
            this.saving = false
            this.$refs.planCalendar.reloadCalendar()
          },
          error => {
            this.apiError = ''
            if (error.response) {
              this.apiError = this.$t(
                'error.' + error.response.data.error.message_key
              )
            }
            this.saving = false
          }
        )
        .then(() => {
          if (this.apiError === null) {
            Toastify({
              node: cash('#success-notification-content')
                .clone()
                .removeClass('hidden')[0],
              duration: 5000,
              newWindow: true,
              close: true,
              gravity: 'top',
              position: 'right',
              stopOnFocus: true
            }).showToast()
            return
          }
          Toastify({
            node: cash('#failed-notification-content')
              .clone()
              .removeClass('hidden')[0],
            duration: 10000,
            newWindow: true,
            close: true,
            gravity: 'top',
            position: 'right',
            stopOnFocus: true
          }).showToast()
        })
    }
  },
  computed: {
    console: () => console
  },
  data() {
    return {
      employee: {},
      apiError: '',
      isRangePlanDeleting: false
    }
  },
  mounted() {
    employeeApi.getDetail(this.$route.params.uuid).then(
      response => {
        this.employee = response.data

        this.loading = false
      },
      error => {
        alert(error)
      }
    )
  },
  setup() {
    const loading = ref(true)
    const saving = ref(false)
    const rangeView = ref(false)
    const dateConstraint = new Date() - 1
    const date = ref('')
    const daterange = ref('')

    const timeValidator = helpers.regex(/^([0-1][0-9]|2[0-4]):[0-5][0-9]$/)
    const formData = reactive({
      timeFrom: '08:00',
      timeTo: '20:00',
      date: dateConstraint
    })
    const rules = {
      timeFrom: {
        required,
        timeValidator
      },
      timeTo: {
        required,
        timeValidator
      },
      date: {},
      dateFrom: {},
      dateTo: {}
    }
    const validate = useVuelidate(rules, toRefs(formData))

    const selectedBranch = ref({})
    const selectedDays = ref({})

    return {
      validate,
      selectedBranch,
      selectedDays,
      formData,
      rangeView,
      loading,
      saving,
      date,
      daterange,
      dateConstraint
    }
  }
})
</script>
